// FP 간트 — 상세 정보 "항목/내용" 행 렌더러 (툴팁 · 상세 패널 공용)
//
// 같은 값을 hover 툴팁(`GanttTooltip`)과 우클릭 상세 패널(`GanttDetailPanel`)이 함께 보여준다.
// 두 곳에 마크업을 복제하면 한쪽만 고쳐 표기가 갈라진다 — 실제로 툴팁이 두 화면에 복제돼 있다가
// 정렬 규칙이 어긋난 적이 있어서 그때 `GanttTooltip`으로 합쳤다(§7-3). 같은 이유로 행 단위 규칙을
// 여기 한 곳에 둔다.
//
// 규칙(근거는 reports/gantt-implementation.md §7-3a/§7-3b):
//   - 라벨/값 2열. 라벨 열 폭은 **가장 긴 라벨**에 맞추고 값은 한 열에서 시작한다.
//   - 글자 크기는 3단계만(`UI.tip*`). 본문은 `cellFontSize`와 같아 캔버스 바 라벨과 어긋나지 않는다.
//   - 코드값에 등폭 글꼴을 쓰지 않는다(한 카드 안에서 글꼴이 섞이면 그 행만 튄다).
//   - WIP 같은 Y/N은 **읽기전용 체크박스** — 상태를 글자로 읽지 않고 형태로 본다.
//   - `codeName` 행은 "코드 굵게 / 명칭 작은 회색" **두 줄 고정**(2026-08-25 추가, 근거는 아래 주석).

import { Fragment } from "react";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";

import {
  LATE_TEXT_BG,
  LATE_TEXT_COLOR,
  ONTIME_TEXT_BG,
  ONTIME_TEXT_COLOR,
  UI
} from "@plannel/pages/factory-plan/common/gantt/ganttConstants";

// 항목/내용 2열 — 이 값이 곧 정렬 규칙이다. `columnGap` 20px: 12·16px에서는 라벨과 값이 붙어 답답해
// 보인다(2026-08-25 사용자 확인). 라벨 열이 `max-content`(가장 긴 라벨 기준)라 짧은 라벨 행은 그만큼
// 더 벌어지지만, 값 열이 한 자리에서 시작하는 정렬이 유지되므로 훑는 데 문제가 없다.
export const DETAIL_GRID = {
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  columnGap: 2.5,
  rowGap: 0.25,
  alignItems: "baseline"
};

// 상태 강조색 — 값 글자(fg)와 뱃지 배경(bg)을 짝으로 둔다. 값은 전부 `ganttConstants` 토큰이다.
export const TONE_COLORS = {
  warn: { fg: LATE_TEXT_COLOR, bg: LATE_TEXT_BG },
  ok: { fg: ONTIME_TEXT_COLOR, bg: ONTIME_TEXT_BG }
};

const LABEL_SX = { color: "text.secondary", whiteSpace: "nowrap", fontSize: UI.tipBodyFontSize };
const VALUE_SX = { whiteSpace: "nowrap", color: UI.tipValueFg, fontSize: UI.tipBodyFontSize };

// 값을 두 줄로 그린다 — 위(코드)는 본문 그대로, 아래(명칭)는 한 단 작은 회색.
// **폭에 걸려 우연히 접히는 것과 다르다**: 이 형태의 값은 길이와 무관하게 항상 같은 모양이 되어
// 행 높이가 규칙적이고(20/34px 혼재가 사라진다) 코드만 훑어 내려가기도 쉽다.
// 아래 줄 값이 비어 있으면 윗줄만 그린다.
//
// ⚠️ **윗줄을 굵게 하지 않는다**(2026-08-25 사용자 확인). 굵기를 주면 그것이 "중요도"가 아니라
// "명칭이 딸린 필드냐"라는 **데이터 구조**를 드러낸다 — 명칭이 없는 작업 주문 코드는 코드로서의
// 성격이 같은데도 보통 굵기라, 굵은 행만 뭔가 특별해 보이는 잘못된 신호가 된다. 상세 패널의 값은
// 전부 동등한 조회 정보이고, 강조가 필요한 상태값(납기 준수/지연)은 뱃지(`chip`)가 따로 맡는다.
// 두 줄 구조와 아래 줄의 크기·색만으로 주/부 관계는 충분히 읽힌다.
const StackedValue = ({ value, subValue, sx }) => (
  <Box sx={{ ...sx, whiteSpace: "normal" }}>
    <Box component="span" sx={{ display: "block" }}>{value ?? ""}</Box>
    {subValue ? (
      <Box
        component="span"
        sx={{ display: "block", color: "text.secondary", fontSize: UI.tipSmallFontSize, mt: "1px" }}
      >
        {subValue}
      </Box>
    ) : null}
  </Box>
);

/**
 * rows: [{ labelKey, value, subValue?, wrap?, checkbox?, tone?, chip? }]
 *   wrap     — 길면 두 줄 허용(긴 코드처럼 한 줄에 안 들어가는 값)
 *   subValue — 값을 두 줄로(위=value, 아래=subValue 작은 회색). 코드/명칭 쌍에 쓴다 —
 *              상세 조회가 코드와 명칭을 **각각** 내려주므로 화면이 그대로 두 칸에 넣으면 된다.
 *              `wrap`보다 우선한다.
 *   checkbox — value를 읽기전용 체크박스로(WIP)
 *   tone     — "warn" | "ok" 강조색
 *   chip     — `tone`과 함께 쓴다. 값을 둥근 배경 뱃지로 그린다(툴팁 헤더 뱃지와 같은 모양).
 *              **상태를 나타내는 낱말**(납기 준수/지연)에만 쓴다 — 날짜·수량처럼 값 자체가 데이터인
 *              곳에 배경을 깔면 읽는 값이 아니라 상태처럼 보인다.
 * small: 한 단 작은 글씨(부가 정보 그룹)
 *
 * ⚠️ **Fragment만 반환한다** — 부모가 grid(또는 subgrid)여야 셀이 두 열에 배치된다.
 */
const GanttDetailRows = ({ rows = [], small = false, t }) => (
  <>
    {rows.map(row => (
      <Fragment key={row.labelKey}>
        <Typography
          variant="caption"
          sx={{
            ...LABEL_SX,
            ...(small ? { fontSize: UI.tipSmallFontSize } : null),
            // 체크박스 행만 라벨도 가운데 정렬 — 컨테이너가 `alignItems: baseline`이라 라벨은 글자
            // 베이스라인에, 체크박스는 자기 `alignSelf: center`에 맞아 서로 어긋나 보였다.
            ...(row.checkbox ? { alignSelf: "center" } : null)
          }}
        >
          {t(row.labelKey)}
        </Typography>

        {row.subValue ? (
          <StackedValue
            value={row.value}
            subValue={row.subValue}
            sx={{ ...VALUE_SX, ...(small ? { fontSize: UI.tipSmallFontSize } : null) }}
          />
        ) : row.checkbox ? (
          // `justifySelf: start`가 없으면 Checkbox 루트(ButtonBase)가 `1fr` 열 전체로 늘어나고 내부적으로
          // 아이콘을 가운데 정렬해서 **값 열 중앙에 떠 보인다**(2026-08-21 수정). 열 시작에 붙인다.
          <Checkbox
            checked={Boolean(row.value)}
            disabled
            size="small"
            sx={{ p: 0, justifySelf: "start", alignSelf: "center", "& .MuiSvgIcon-root": { fontSize: 16 } }}
          />
        ) : (
          <Typography
            variant="caption"
            sx={{
              ...VALUE_SX,
              fontVariantNumeric: "tabular-nums",     // 시각·수량 자리수가 흔들리지 않게
              ...(small ? { fontSize: UI.tipSmallFontSize } : null),
              ...(row.wrap ? { whiteSpace: "normal" } : null),
              ...(row.tone ? { color: TONE_COLORS[row.tone]?.fg, fontWeight: 600 } : null),
              // 뱃지 모양·수치는 툴팁 헤더의 상태 뱃지(GanttTooltip의 KindHeader)와 같은 값을 쓴다 —
              // 같은 뜻("지연"/"준수")이 화면 안에서 두 모양으로 보이지 않게.
              // `justifySelf: start`가 없으면 배경이 `1fr` 열 전체로 늘어난다.
              // 테두리도 헤더 뱃지와 같이 넣는다 — 이 행 뱃지는 밴드가 아닌 흰 배경 위에 놓이지만,
              // **모양이 갈리면 같은 상태가 두 가지로 보인다**(2026-08-26, 사유는 GanttTooltip 쪽 주석).
              ...(row.chip && row.tone ? {
                justifySelf: "start", px: 1, py: 0.25, borderRadius: 999,
                backgroundColor: TONE_COLORS[row.tone]?.bg,
                border: "1px solid", borderColor: TONE_COLORS[row.tone]?.fg
              } : null)
            }}
          >
            {row.value ?? ""}
          </Typography>
        )}
      </Fragment>
    ))}
  </>
);

// 그룹 하나를 독립 그리드로 감싸 그린다(패널처럼 구역마다 열 폭을 공유할 필요가 없는 곳용).
export const GanttDetailGroup = ({ rows, small, t, sx }) => (
  <Box sx={{ ...DETAIL_GRID, ...sx }}>
    <GanttDetailRows rows={rows} small={small} t={t} />
  </Box>
);

export default GanttDetailRows;
